import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ArrowLeft,
  Power,
  Thermometer,
  Lock,
  Unlock,
  Sun,
  Activity,
  Info,
  Settings,
  RefreshCw,
  Clock,
  MapPin,
  Cpu,
  Wifi,
  Battery,
  Calendar
} from 'lucide-react';
import smartThingsService from '../services/smartThingsService';
import smartThingsBackendService from '../services/smartThingsBackendService';
import { IoTDevice } from '../models/iotModels';
import './DeviceDetails.css';

const DeviceDetails = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('control');
  const [deviceHistory, setDeviceHistory] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  // Control states
  const [brightness, setBrightness] = useState(50);
  const [temperature, setTemperature] = useState(22);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    loadDeviceDetails();
    loadDeviceHistory();
  }, [deviceId]);

  const loadDeviceDetails = async () => {
    setLoading(true);
    try {
      // Get device from SmartThings
      const deviceData = await smartThingsService.getDeviceStatus(deviceId);
      
      // Get database device info
      let dbDevice = null;
      try {
        dbDevice = await smartThingsBackendService.getDeviceById(deviceId);
      } catch (err) {
        console.warn('Device not in database:', err);
      }

      // Merge data
      const iotDevice = new IoTDevice(deviceData);
      if (dbDevice) {
        iotDevice.roomId = dbDevice.room_id;
        iotDevice.hotelId = dbDevice.hotel_id;
      }

      setDevice(iotDevice);

      // Initialize controls from current state
      if (iotDevice.hasCapability('switchLevel')) {
        const level = iotDevice.getState('switchLevel', 'level');
        setBrightness(level || 50);
      }
      if (iotDevice.hasCapability('thermostat')) {
        const temp = iotDevice.getState('thermostat', 'temperature');
        setTemperature(temp || 22);
      }
    } catch (error) {
      console.error('Failed to load device:', error);
      toast.error('Failed to load device details');
      navigate('/iot-management');
    } finally {
      setLoading(false);
    }
  };

  const loadDeviceHistory = async () => {
    try {
      // Mock history data - replace with actual API call when available
      setDeviceHistory([
        {
          id: 1,
          action: 'Device turned on',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
          icon: 'power',
          color: 'success'
        },
        {
          id: 2,
          action: 'Brightness set to 75%',
          timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000),
          icon: 'brightness',
          color: 'info'
        },
        {
          id: 3,
          action: 'Device turned off',
          timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000),
          icon: 'power',
          color: 'secondary'
        },
        {
          id: 4,
          action: 'Temperature adjusted to 22°C',
          timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
          icon: 'temperature',
          color: 'warning'
        }
      ]);
    } catch (error) {
      console.error('Failed to load device history:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDeviceDetails();
    await loadDeviceHistory();
    setTimeout(() => setRefreshing(false), 500);
    toast.success('Device data refreshed');
  };

  const handleTogglePower = async () => {
    if (!device || !device.isOnline()) {
      toast.error('Device is offline');
      return;
    }

    setIsUpdating(true);
    try {
      const currentState = device.getState('switch', 'switch');
      const newState = currentState === 'on' ? 'off' : 'on';

      await smartThingsService.executeDeviceCommand(
        deviceId,
        'switch',
        newState
      );

      toast.success(`Device turned ${newState}`);
      await loadDeviceDetails();
    } catch (error) {
      console.error('Failed to toggle device:', error);
      toast.error('Failed to toggle device');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSetBrightness = async (value) => {
    if (!device || !device.isOnline()) {
      toast.error('Device is offline');
      return;
    }

    setBrightness(value);

    // Debounce the actual command
    clearTimeout(window.brightnessTimeout);
    window.brightnessTimeout = setTimeout(async () => {
      setIsUpdating(true);
      try {
        await smartThingsService.executeDeviceCommand(
          deviceId,
          'setLevel',
          [value]
        );
        toast.success(`Brightness set to ${value}%`);
        await loadDeviceDetails();
      } catch (error) {
        console.error('Failed to set brightness:', error);
        toast.error('Failed to set brightness');
      } finally {
        setIsUpdating(false);
      }
    }, 500);
  };

  const handleSetTemperature = async (newTemp) => {
    if (!device || !device.isOnline()) {
      toast.error('Device is offline');
      return;
    }

    setIsUpdating(true);
    try {
      await smartThingsService.executeDeviceCommand(
        deviceId,
        'setHeatingSetpoint',
        [newTemp]
      );
      setTemperature(newTemp);
      toast.success(`Temperature set to ${newTemp}°C`);
      await loadDeviceDetails();
    } catch (error) {
      console.error('Failed to set temperature:', error);
      toast.error('Failed to set temperature');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleLock = async () => {
    if (!device || !device.isOnline()) {
      toast.error('Device is offline');
      return;
    }

    setIsUpdating(true);
    try {
      const currentState = device.getState('lock', 'lock');
      const command = currentState === 'locked' ? 'unlock' : 'lock';

      await smartThingsService.executeDeviceCommand(
        deviceId,
        command
      );

      toast.success(`Device ${command}ed`);
      await loadDeviceDetails();
    } catch (error) {
      console.error('Failed to toggle lock:', error);
      toast.error('Failed to toggle lock');
    } finally {
      setIsUpdating(false);
    }
  };

  const formatTimestamp = (date) => {
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const getHistoryIcon = (iconName) => {
    const icons = {
      power: <Power size={16} />,
      brightness: <Sun size={16} />,
      temperature: <Thermometer size={16} />,
      lock: <Lock size={16} />
    };
    return icons[iconName] || <Activity size={16} />;
  };

  if (loading) {
    return (
      <div className="device-details-loading">
        <div className="spinner"></div>
        <p>Loading device details...</p>
      </div>
    );
  }

  if (!device) {
    return (
      <div className="device-details-error">
        <p>Device not found</p>
        <button onClick={() => navigate('/iot-management')} className="btn-primary">
          Back to IoT Management
        </button>
      </div>
    );
  }

  return (
    <div className="device-details-container">
      {/* Header */}
      <div className="device-details-header">
        <button onClick={() => navigate('/iot-management')} className="btn-back">
          <ArrowLeft size={20} />
          <span>Back to Devices</span>
        </button>
        <button onClick={handleRefresh} className="btn-icon" disabled={refreshing}>
          <RefreshCw size={18} className={refreshing ? 'spinning' : ''} />
        </button>
      </div>

      {/* Device Info Card */}
      <div className={`device-info-card ${device.isOnline() ? 'online' : 'offline'}`}>
        <div className="device-info-icon">
          <span className="device-icon-large">{device.getIcon()}</span>
          <span className={`status-badge ${device.status}`}>
            {device.status}
          </span>
        </div>
        <div className="device-info-content">
          <h1>{device.name}</h1>
          <p className="device-type">{device.getTypeLabel()}</p>
          <div className="device-meta">
            <span className="device-meta-item">
              <MapPin size={14} />
              {device.roomId || 'Unassigned'}
            </span>
            <span className="device-meta-item">
              <Cpu size={14} />
              {device.id}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="device-tabs">
        <button
          className={`tab ${activeTab === 'control' ? 'active' : ''}`}
          onClick={() => setActiveTab('control')}
        >
          <Settings size={16} />
          Control
        </button>
        <button
          className={`tab ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          <Clock size={16} />
          History
        </button>
        <button
          className={`tab ${activeTab === 'info' ? 'active' : ''}`}
          onClick={() => setActiveTab('info')}
        >
          <Info size={16} />
          Info
        </button>
      </div>

      {/* Tab Content */}
      <div className="device-tab-content">
        {/* Control Tab */}
        {activeTab === 'control' && (
          <div className="control-tab">
            <h2>Device Controls</h2>

            {/* Power Control */}
            {device.hasCapability('switch') && (
              <div className="control-card">
                <div className="control-card-header">
                  <Power size={20} />
                  <span>Power</span>
                </div>
                <div className="control-card-body">
                  <p className="control-status">
                    Status: <strong>{device.getState('switch', 'switch') || 'unknown'}</strong>
                  </p>
                  <button
                    onClick={handleTogglePower}
                    disabled={!device.isOnline() || isUpdating}
                    className={`btn-control-large ${
                      device.getState('switch', 'switch') === 'on' ? 'btn-success' : 'btn-secondary'
                    }`}
                  >
                    <Power size={20} />
                    {device.getState('switch', 'switch') === 'on' ? 'Turn Off' : 'Turn On'}
                  </button>
                </div>
              </div>
            )}

            {/* Brightness Control */}
            {device.hasCapability('switchLevel') && (
              <div className="control-card">
                <div className="control-card-header">
                  <Sun size={20} />
                  <span>Brightness</span>
                </div>
                <div className="control-card-body">
                  <p className="control-status">
                    Current: <strong>{brightness}%</strong>
                  </p>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={brightness}
                    onChange={(e) => handleSetBrightness(parseInt(e.target.value))}
                    disabled={!device.isOnline() || isUpdating}
                    className="brightness-slider"
                  />
                  <div className="brightness-labels">
                    <span>0%</span>
                    <span>50%</span>
                    <span>100%</span>
                  </div>
                </div>
              </div>
            )}

            {/* Temperature Control */}
            {device.hasCapability('thermostat') && (
              <div className="control-card">
                <div className="control-card-header">
                  <Thermometer size={20} />
                  <span>Temperature</span>
                </div>
                <div className="control-card-body">
                  <div className="temperature-display">
                    <span className="temperature-value">{temperature}°C</span>
                    <span className="temperature-current">
                      Current: {device.getState('thermostat', 'temperature') || '--'}°C
                    </span>
                  </div>
                  <div className="temperature-controls">
                    <button
                      onClick={() => handleSetTemperature(temperature - 0.5)}
                      disabled={!device.isOnline() || isUpdating}
                      className="btn-temp"
                    >
                      −
                    </button>
                    <button
                      onClick={() => handleSetTemperature(temperature + 0.5)}
                      disabled={!device.isOnline() || isUpdating}
                      className="btn-temp"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Lock Control */}
            {device.hasCapability('lock') && (
              <div className="control-card">
                <div className="control-card-header">
                  {device.getState('lock', 'lock') === 'locked' ? (
                    <Lock size={20} />
                  ) : (
                    <Unlock size={20} />
                  )}
                  <span>Lock</span>
                </div>
                <div className="control-card-body">
                  <p className="control-status">
                    Status: <strong>{device.getState('lock', 'lock') || 'unknown'}</strong>
                  </p>
                  <button
                    onClick={handleToggleLock}
                    disabled={!device.isOnline() || isUpdating}
                    className={`btn-control-large ${
                      device.getState('lock', 'lock') === 'locked' ? 'btn-warning' : 'btn-success'
                    }`}
                  >
                    {device.getState('lock', 'lock') === 'locked' ? (
                      <>
                        <Unlock size={20} />
                        Unlock
                      </>
                    ) : (
                      <>
                        <Lock size={20} />
                        Lock
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {!device.hasCapability('switch') &&
              !device.hasCapability('switchLevel') &&
              !device.hasCapability('thermostat') &&
              !device.hasCapability('lock') && (
                <div className="no-controls">
                  <Info size={48} />
                  <p>No controls available for this device</p>
                </div>
              )}
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="history-tab">
            <h2>Recent Activity</h2>
            <div className="history-timeline">
              {deviceHistory.map((item) => (
                <div key={item.id} className={`history-item history-${item.color}`}>
                  <div className="history-icon">{getHistoryIcon(item.icon)}</div>
                  <div className="history-content">
                    <p className="history-action">{item.action}</p>
                    <p className="history-timestamp">{formatTimestamp(item.timestamp)}</p>
                  </div>
                </div>
              ))}
            </div>
            {deviceHistory.length === 0 && (
              <div className="no-history">
                <Clock size={48} />
                <p>No activity history available</p>
              </div>
            )}
          </div>
        )}

        {/* Info Tab */}
        {activeTab === 'info' && (
          <div className="info-tab">
            <h2>Device Information</h2>

            {/* General Info */}
            <div className="info-section">
              <h3>General</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Device ID</span>
                  <span className="info-value">{device.id}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Device Name</span>
                  <span className="info-value">{device.name}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Type</span>
                  <span className="info-value">{device.getTypeLabel()}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Status</span>
                  <span className={`info-badge info-badge-${device.status}`}>{device.status}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Room</span>
                  <span className="info-value">{device.roomId || 'Unassigned'}</span>
                </div>
              </div>
            </div>

            {/* Hardware Info */}
            {(device.manufacturer || device.model) && (
              <div className="info-section">
                <h3>Hardware</h3>
                <div className="info-grid">
                  {device.manufacturer && (
                    <div className="info-item">
                      <span className="info-label">Manufacturer</span>
                      <span className="info-value">{device.manufacturer}</span>
                    </div>
                  )}
                  {device.model && (
                    <div className="info-item">
                      <span className="info-label">Model</span>
                      <span className="info-value">{device.model}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Status Info */}
            <div className="info-section">
              <h3>Status</h3>
              <div className="info-grid">
                {device.batteryLevel !== undefined && (
                  <div className="info-item">
                    <span className="info-label">
                      <Battery size={14} />
                      Battery Level
                    </span>
                    <div className="battery-indicator">
                      <div
                        className="battery-fill"
                        style={{
                          width: `${device.batteryLevel}%`,
                          backgroundColor: device.batteryLevel > 20 ? '#4caf50' : '#f44336'
                        }}
                      ></div>
                      <span className="battery-text">{device.batteryLevel}%</span>
                    </div>
                  </div>
                )}
                {device.signalStrength !== undefined && (
                  <div className="info-item">
                    <span className="info-label">
                      <Wifi size={14} />
                      Signal Strength
                    </span>
                    <span className="info-value">{device.signalStrength} dBm</span>
                  </div>
                )}
              </div>
            </div>

            {/* Capabilities */}
            {device.capabilities && device.capabilities.length > 0 && (
              <div className="info-section">
                <h3>Capabilities</h3>
                <div className="capabilities-list">
                  {device.capabilities.map((cap, index) => (
                    <span key={index} className="capability-chip">
                      {cap}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Timestamps */}
            <div className="info-section">
              <h3>Timestamps</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">
                    <Calendar size={14} />
                    Last Updated
                  </span>
                  <span className="info-value">
                    {device.lastUpdated ? new Date(device.lastUpdated).toLocaleString() : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeviceDetails;
